import type { Content, Editor, EditorEvents, Extensions, FocusPosition } from '@tiptap/react';
import type { ReactNode } from 'react';

import DragHandle from '@tiptap/extension-drag-handle-react';
import { Placeholder } from '@tiptap/extensions';
import { EditorContent, EditorContext, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { GripVerticalIcon } from 'lucide-react';
import { useEffect } from 'react';

import { Button } from '@/components/ui/button';
import { defaultExtensionSet } from '@/lib/tiptap-extensions-sets';

interface TiptapProps {
  content?: Content;
  autofocus?: FocusPosition;
  editable?: boolean;
  onUpdate?: (props: EditorEvents['update']) => void;
  onEditorReady?: (editor: Editor | null) => void;
  extensions?: Extensions;
  placeholder?: string;
  children?: ReactNode;
  className?: string;
}

/**
 * The main Tiptap editor component.
 * It initializes the Tiptap editor with a set of extensions and provides it
 * to child components via React Context. It renders the toolbar and the editor content area.
 * @param tiptapProps - Props for the Tiptap component, including editor options and custom extensions.
 */
export function Tiptap(tiptapProps: TiptapProps) {
  const { extensions = defaultExtensionSet, placeholder, editable = true, children, className, onEditorReady, ...props } = tiptapProps;

  const editor: Editor | null = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        code: false,
        trailingNode: false,
        link: false,
        hardBreak: false,
      }),
      Placeholder.configure({
        placeholder: placeholder || 'Wprowadź treść...',
        showOnlyWhenEditable: true,
      }),
      ...extensions,
    ],
    editable: editable,

    ...props,
  });

  useEffect(() => {
    onEditorReady?.(editor);
  }, [editor, onEditorReady]);

  return (
    <EditorContext value={{ editor }}>
      {children}
      {editable && editor && (
        <DragHandle editor={editor} className="pe-1">
          <Button type={'button'} variant={'tertiary'} size={'sm'} className={'h-7 min-h-7 w-5 min-w-5'}>
            <GripVerticalIcon className="stroke-muted-foreground size-3.5" />
          </Button>
        </DragHandle>
      )}
      <EditorContent editor={editor} role="presentation" className={className} />
    </EditorContext>
  );
}
